import type { MealPlanSummaryView } from '@steado/admin-contract/user-detail';

import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { BadgeCell } from '@/components/table/cells';
import { formatDate, formatLocalDate } from '@/lib/format';
import { planTone } from '@/lib/plan-tone';

import { MEAL_PLAN_ENDED_BY_LABELS } from './plan-renewal-labels';

/**
 * The Premium meal plan, beside the weekly week rather than folded into it.
 *
 * They are two rows with two lifetimes — a meal plan runs one to seven days
 * from any day, a week runs seven from a Monday — and a user holding both was
 * reading as a user holding only the week.
 *
 * `endDate` is the server's, derived from the start and the day count;
 * `endedAt` is when stopping was recorded. Both are shown because an expired
 * plan can sit with a null `endedAt` for days, and the pair is the difference
 * between "it has run out" and "nothing has picked it up yet".
 */
export function MealPlanPanel({ mealPlan }: { readonly mealPlan: MealPlanSummaryView | null }) {
  if (mealPlan === null) {
    return (
      <Panel title="Meal plan">
        <p className="text-[13px] text-muted-foreground">Never made one.</p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Meal plan"
      action={<BadgeCell label={mealPlan.status} tone={planTone(mealPlan.status)} dot />}
      bleed
    >
      <FieldRows>
        <FieldRow
          label="Runs"
          value={`${formatLocalDate(mealPlan.weekStartDate)} → ${formatLocalDate(mealPlan.endDate)}`}
        />
        <FieldRow label="Days" value={String(mealPlan.dayCount)} mono />
        <FieldRow label="Ended" value={endedValue(mealPlan)} />
      </FieldRows>
    </Panel>
  );
}

function endedValue(mealPlan: MealPlanSummaryView): string | null {
  if (mealPlan.endedAt === null) return null;

  const when = formatDate(mealPlan.endedAt);
  return mealPlan.endedBy === null ? when : `${when} · ${MEAL_PLAN_ENDED_BY_LABELS[mealPlan.endedBy]}`;
}
